import {Component, Input, inject} from '@angular/core';
import {DefaultValues, MODULE_NAMES, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {
  EdgeArrowTypeMapVisual,
  EdgeShapeMapVisual,
  EdgeTypeMapVisual,
  GraphNodeLink,
  LinkDirectionMap,
  LinkLabelSlopeMap
} from '../../../api/interfaces/graph-node.interface';
import {ColorInputComponent} from '../../../shared/forms/color-input/color-input.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HasModuleDirective} from '../../../packages/has-module.directive';
import {SelectInputComponent} from '../../../shared/forms/select-input/select-input.component';
import {SliderInputComponent} from '../../../shared/forms/slider-input/slider-input.component';
import {TextInputComponent} from '../../../shared/forms/text-input/text-input.component';
import {AudioReachabilityDirective} from '../../../shared/directives/url-validator/audio-url-validator.directive';
import {TranslatePipe} from '@ngx-translate/core';
import {HasPackageDirective} from '../../../packages/has-package.directive';
import {PackageService} from '../../../api/services/package.service';
import {CytoscapeService} from '../../../services/cy-services/cytoscape.service';
import {StyleGroupExpansionService} from '../../../shared/style-groups/style-group-expansion.service';
import {coreStyleGroupId, styleGroupToggleTooltip} from '../../../shared/style-groups/style-group.util';
import {StyleRowComponent} from '../../../shared/style-groups/style-row.component';

/**
 * MAP-990: link STYLES section, reorganized into core styles (color, shape,
 * directionality, label font size, sound effect) each individually
 * expandable/collapsible to reveal their dependent styles, replacing the old
 * "light" (always visible) / "advanced" (behind a more/less button) split —
 * see LinkStyleAdvancedComponent's former content, now merged in here in the
 * ticket's required order. "color" and "sound effect" have no dependents, so
 * they render no toggle at all (AC4).
 */
@Component({
  styles: [`
    /* MAP-1533: left-align the leading style icons so they start at the same
       horizontal position as the STYLES section header (like PROPERTIES and
       COMMENTS above), instead of centered in their column. Scoped here only. */
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex {
      justify-content: flex-start !important;
      /* MAP-1533 round 2: a bootstrap col-2 (16.7% of the row) is far wider
         than the icon glyph needs; even left-aligned, the leftover width
         inside this column was a large dead gap before the field starts,
         narrowing the field itself. Give the icon a fixed footprint and let
         the field that follows reclaim the rest. */
      flex: 0 0 28px !important;
      max-width: 28px !important;
      width: 28px !important;
    }
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex + [class*="col-"] {
      flex: 1 1 0% !important;
      max-width: none !important;
      width: auto !important;
    }
  `],
  template: `
    <ng-container [formGroup]="formGroup">
      <!-- COLOR (core, no dependents -> no toggle) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.LINK_COLOR" [divider]="false">
        <app-color-input [value]="focusedLink?.color || DefaultValues.link.color"
                         [placeholder]="'LINKS.SET_COLOR' | translate"
                         [icon]="'palette'"
                         formControlName="color"></app-color-input>
      </maporium-style-row>

      <!-- SHAPE (core) -> shape thickness, shape stroke (dependents) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.LINK_SHAPING"
                          [hasToggle]="true"
                          [expanded]="isExpanded('shape')"
                          [tooltip]="toggleTooltip('shape')"
                          (toggleClick)="toggle('shape')">
        <app-select-input formControlName="type"
                          [label]="'moving'"
                          [placeholder]="'LINKS.SET_SHAPE' | translate"
                          [isIcon]="true"
                          [options]="edgeTypesVisual">
        </app-select-input>
      </maporium-style-row>
      @if (isExpanded('shape')) {
        <maporium-style-row [divider]="false">
          <app-slider-input [label]="'line_weight'"
                            [value]="toNumber(focusedLink!.weight)"
                            [isIcon]="true"
                            [placeholder]="'LINKS.SET_THICKNESS' | translate"
                            formControlName="weight"
                            [max]="32"
                            [displayMultiplier]="2"
                            [min]="0"
                            [step]="1">
          </app-slider-input>
        </maporium-style-row>
        <maporium-style-row [divider]="false" *appHasModule="MODULE_NAMES.LINK_STROKE">
          <app-select-input formControlName="shape"
                            [label]="'line_style'"
                            [placeholder]="'LINKS.SET_STROKE' | translate"
                            [isIcon]="true"
                            [options]="edgeShapesVisual">
          </app-select-input>
        </maporium-style-row>
      }

      <!-- DIRECTIONALITY (core) -> head style (dependent) -->
      <maporium-style-row *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]"
                          [hasToggle]="true"
                          [expanded]="isExpanded('directionality')"
                          [tooltip]="toggleTooltip('directionality')"
                          (toggleClick)="toggle('directionality')">
        <app-select-input formControlName="direction"
                          [label]="'arrow_range'"
                          [placeholder]="'LINKS.SET_DIRECTIONALITY' | translate"
                          [isIcon]="true"
                          [options]="linkDirection">
        </app-select-input>
      </maporium-style-row>
      @if (isExpanded('directionality')) {
        <maporium-style-row [divider]="false">
          <app-select-input formControlName="edgeArrowType"
                            [label]="'line_end'"
                            [placeholder]="'Set the link\\'s directionality head style' | translate"
                            [isIcon]="true"
                            [options]="edgeArrowTypeVisual">
          </app-select-input>
        </maporium-style-row>
      }

      <!-- LABEL FONT SIZE (core) -> label property, label slope, label alignment, label distance (dependents) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.LINK_LABEL_ADJUSTMENT"
                          [hasToggle]="true"
                          [expanded]="isExpanded('labelFontSize')"
                          [tooltip]="toggleTooltip('label font size')"
                          (toggleClick)="toggle('labelFontSize')">
        <app-slider-input [label]="'format_size'"
                          [value]="toNumber(focusedLink?.textFontSize !== undefined ? focusedLink?.textFontSize : DefaultValues.link.textFontSize)"
                          [isIcon]="true"
                          [placeholder]="'LINKS.SET_FONT_SIZE' | translate"
                          formControlName="textFontSize"
                          [discrete]="true"
                          [max]="32"
                          [min]="0"
                          [step]="1">
        </app-slider-input>
      </maporium-style-row>
      @if (isExpanded('labelFontSize')) {
        <maporium-style-row [divider]="false" *ngIf="packageService.isOnTestPlan()">
          <app-select-input formControlName="label"
                            [label]="'label'"
                            [isIcon]="true"
                            [noIcons]="true"
                            [truncate]="true"
                            [metadata]="metadata"
                            [placeholder]="'LINKS.SET_LABEL' | translate">
          </app-select-input>
        </maporium-style-row>
        <maporium-style-row [divider]="false"
                            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
          <app-select-input formControlName="labelSlope"
                            [label]="'text_rotation_angleup'"
                            [placeholder]="'Set the slope of the link\\'s label'"
                            [isIcon]="true"
                            [options]="linkLabelSlopeMap">
          </app-select-input>
        </maporium-style-row>
        <maporium-style-row [divider]="false"
                            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
          <app-slider-input formControlName="linkLabelPosition"
                            [min]="-1"
                            [max]="1"
                            [step]="0.01"
                            [label]="'format_letter_spacing_2'"
                            [value]="focusedLink?.linkLabelPosition ?? 0"
                            [isIcon]="true"
                            [placeholder]="'Set the alignment of the link\\'s label'"
                            [discrete]="true"
                            [showTickMarks]="false"
                            (immediateValueChange)="onLinkLabelPositionChange($event)">
          </app-slider-input>
        </maporium-style-row>
        <maporium-style-row [divider]="false"
                            *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
          <app-slider-input formControlName="linkLabelOffset"
                            [min]="-16"
                            [max]="16"
                            [step]="1"
                            [label]="'text_up'"
                            [value]="focusedLink?.linkLabelOffset ?? DefaultValues.link.linkLabelOffset"
                            [isIcon]="true"
                            [placeholder]="'Set the distance of the link\\'s label'"
                            [discrete]="true"
                            (immediateValueChange)="onLinkLabelOffsetChange($event)">
          </app-slider-input>
        </maporium-style-row>
      }

      <!-- SOUND EFFECT (core, no dependents -> no toggle) -->
      <maporium-style-row *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
        <app-text-input formControlName="soundUrl"
                        [placeholder]="'Sound URL'"
                        [tooltip]="'Set the link\\'s sound effect via a sound URL'"
                        appAudioReachability
                        [autoFocusAndSelect]="true"
                        [label]="'graphic_eq'"
                        [isIcon]="true"></app-text-input>
      </maporium-style-row>
    </ng-container>
  `,
  selector: 'maporium-link-style',
  standalone: true,
  imports: [
    ColorInputComponent,
    FormsModule,
    HasModuleDirective,
    ReactiveFormsModule,
    SelectInputComponent,
    SliderInputComponent,
    TextInputComponent,
    AudioReachabilityDirective,
    TranslatePipe,
    HasPackageDirective,
    StyleRowComponent,
  ]
})
export class LinkStyleComponent {
  protected readonly MODULE_NAMES = MODULE_NAMES;
  protected readonly DefaultValues = DefaultValues;
  protected readonly edgeTypesVisual = EdgeTypeMapVisual;
  protected readonly edgeShapesVisual = EdgeShapeMapVisual;
  protected readonly linkDirection = LinkDirectionMap;
  protected readonly edgeArrowTypeVisual = EdgeArrowTypeMapVisual;
  protected readonly linkLabelSlopeMap = LinkLabelSlopeMap;

  @Input() formGroup: any;
  @Input() metadata: any;
  @Input() focusedLink: GraphNodeLink | undefined;

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
  protected packageService = inject(PackageService);
  private cytoscapeService = inject(CytoscapeService);
  private styleGroups = inject(StyleGroupExpansionService);

  toNumber(value: any) {
    return value !== undefined && value !== null ? Number(value) : 0;
  }

  isExpanded(coreStyleId: string): boolean {
    return this.styleGroups.isExpanded(`link.${coreStyleId}`);
  }

  toggle(coreStyleId: string): void {
    this.styleGroups.toggle(`link.${coreStyleId}`);
  }

  toggleTooltip(coreStyleName: string): string {
    return styleGroupToggleTooltip(coreStyleName, this.isExpanded(coreStyleGroupId(coreStyleName)));
  }

  onLinkLabelPositionChange(value: number): void {
    const cy = this.cytoscapeService.cy;
    const selectedEdges = cy ? cy.edges(':selected') : [];

    if (selectedEdges.length > 1) {
      selectedEdges.forEach(edge => {
        const linkId = edge.id();
        this.cytoscapeService.updateLinkLabelPositionVisualOnly(linkId, value);
      });
    } else if (this.focusedLink?.id) {
      this.cytoscapeService.updateLinkLabelPositionVisualOnly(this.focusedLink.id, value);
    }
  }

  onLinkLabelOffsetChange(value: number): void {
    const cy = this.cytoscapeService.cy;
    const selectedEdges = cy ? cy.edges(':selected') : [];

    if (selectedEdges.length > 1) {
      selectedEdges.forEach(edge => {
        const linkId = edge.id();
        this.cytoscapeService.updateLinkLabelOffsetVisualOnly(linkId, value);
      });
    } else if (this.focusedLink?.id) {
      this.cytoscapeService.updateLinkLabelOffsetVisualOnly(this.focusedLink.id, value);
    }
  }
}
